/**
 * B632 (b) — the tenant's SELF-SERVICE rule for guests (what a buyer may do
 * to their own booking without calling the venue). Read by the books seat
 * (the ops.config.get audience), written by ADMIN on the tenant-config
 * scope (the ops.config.set wall). Flat fields, not a union, so every door
 * (the MCP membrane included) translates it; the domain names each problem.
 * The answer carries the guest's sentences so every face says the rule in
 * one voice.
 */
import { z } from "zod";
import { readOp, writeOp, zEpochMs } from "./common.js";

const zPolicyFields = {
  refunds: z.enum(["NONE", "FULL", "WINDOW"]),
  refundHoursBefore: z.number().int().min(1).max(720).optional(),
  refundKeepBps: z.number().int().min(0).max(10_000).optional(),
  changes: z.enum(["NONE", "SAME_SHOW", "ANY_SHOW"]),
  addTickets: z.boolean(),
  resend: z.boolean(),
};

/**
 * B632 (c) region 2 — the rule as the GUEST reads it, on `order.get` (their
 * own order, under the CUSTOMER fence): the flags and the guest's sentences.
 * Not `source`/`updatedAt` — where a setting came from is the books seat's.
 */
export const zSelfServiceRule = z.object({ ...zPolicyFields, sentences: z.array(z.string()) }).strict();

const zPolicyBody = z
  .object({
    ...zPolicyFields,
    /** Where the answer came from: the platform default, or the tenant's own setting. */
    source: z.enum(["defaults", "config"]),
    /** What a guest reads, one sentence per rule. */
    sentences: z.array(z.string()),
    updatedAt: zEpochMs.optional(),
  })
  .strict();

export const selfServicePolicyGet = readOp({
  name: "selfservice.policy.get",
  permission: "ledger:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: {},
  response: zPolicyBody,
  exampleRequest: {},
  exampleResponse: {
    refunds: "NONE",
    changes: "NONE",
    addTickets: false,
    resend: true,
    source: "defaults",
    sentences: ["Refunds aren’t available online — contact us.", "Bookings can’t be moved online.", "You can re-send your tickets to yourself any time."],
  },
});

export const selfServicePolicySet = writeOp({
  name: "selfservice.policy.set",
  // Tenant configuration write — the channel-config scope (ADMIN-only), the ops.config.set wall.
  permission: "channel:write",
  principals: ["STAFF:ADMIN"],
  fields: zPolicyFields,
  response: zPolicyBody,
  exampleRequest: { refunds: "WINDOW", refundHoursBefore: 24, changes: "SAME_SHOW", addTickets: true, resend: true },
  exampleResponse: {
    refunds: "WINDOW",
    refundHoursBefore: 24,
    changes: "SAME_SHOW",
    addTickets: true,
    resend: true,
    source: "config",
    sentences: [
      "You can refund your tickets online up to 24 hours before the show.",
      "You can move your booking to another showtime of the same show.",
      "You can add tickets to your booking.",
      "You can re-send your tickets to yourself any time.",
    ],
    updatedAt: 1_790_000_000_000,
  },
});
